import {useQuery} from 'react-query';
import {isNotNullOrWhiteSpace} from '../lib/isNullOrWhiteSpace.ts';
import isValidURL from '../lib/isValidURL.ts';
import useIframeCheck from '../api/web_content/useIframeCheck.ts';
import isSuccessStatusCode from '../lib/isSuccessStatusCode.ts';
import {parseGoogleWorkspaceLink} from '../lib/googleWorkspaceLinks.ts';

export interface EmbeddedWebPageCheckResult {
    isLoading: boolean;
    isError: boolean;
    isValid: boolean;
    message: string | null;
    /**
     * When the page can't be framed but is a Google Workspace file, a link
     * that people who do have access can open directly.
     */
    openUrl: string | null;
}

const useEmbeddedWebPageCheck = (
    url: string | null | undefined
): EmbeddedWebPageCheckResult => {
    const urlProvided = isNotNullOrWhiteSpace(url);
    const isValidUrl = urlProvided && isValidURL(url ?? '');
    const iframeCheck = useIframeCheck({url: url ?? ''});

    const {
        data: contentCheckResult,
        isLoading,
        isError
    } = useQuery(['iframeCheck', url], iframeCheck, {
        enabled: isValidUrl
    });

    const isValid = Boolean(
        !isLoading &&
            !isError &&
            isValidUrl &&
            contentCheckResult &&
            isSuccessStatusCode(contentCheckResult.status) &&
            !contentCheckResult.blocked
    );

    const googleLink = parseGoogleWorkspaceLink(url);

    const message = (() => {
        if (!urlProvided) {
            return null;
        }

        if (!isValidUrl) {
            return 'Invalid URL';
        }

        // A Google file that Google won't hand to us is almost always one of
        // two things: it isn't shared with "Anyone with the link", or it is an
        // editing link rather than the embed form. Say that instead of the
        // raw HTTP wording, for the viewer and for the owner alike.
        if (
            googleLink &&
            contentCheckResult &&
            (contentCheckResult.blocked ||
                !isSuccessStatusCode(contentCheckResult.status))
        ) {
            return googleLink.isEmbedLink
                ? `This ${googleLink.label} file isn't shared publicly, so it can't be shown here. If it's yours, open it in Google, choose Share, and set General access to "Anyone with the link".`
                : `This ${googleLink.label} file can't be shown here: it's an editing link, which Google only shows to people signed in with access. If it's yours, edit this section and use the embed link, and set the file's sharing to "Anyone with the link".`;
        }

        if (contentCheckResult) {
            if (contentCheckResult.blocked) {
                return (
                    contentCheckResult.reason ||
                    'Sorry, the website does not permit the embedding of web pages in frames.'
                );
            }

            switch (contentCheckResult.status) {
                case 200:
                case 201:
                case 204:
                    return null;
                case 400:
                    return 'Bad Request: The request was invalid or cannot be served.';
                case 401:
                    return 'Unauthorized: Authentication is required to access the resource.';
                case 403:
                    return 'Forbidden: Access to the resource is denied.';
                case 404:
                    return 'Not Found: The requested resource could not be found.';
                case 500:
                    return 'Internal Server Error: An unexpected condition was encountered.';
                case 503:
                    return 'Service Unavailable: The server is currently unable to handle the request.';
                default:
                    return 'Sorry, an error occurred while loading the page.';
            }
        }

        if (isLoading) {
            return null;
        }

        return 'Sorry, page could not be loaded or the website does not permit the embedding of web pages in frames.';
    })();

    return {
        isLoading,
        isError,
        isValid,
        message,
        openUrl: !isValid && googleLink ? googleLink.openUrl : null
    };
};

export default useEmbeddedWebPageCheck;
